import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, filter, fromEvent, map, switchMap, timer } from 'rxjs';
import { APP_CONFIG } from '@core/config/app-config.token';
import { jobsKey } from '@core/services/kiosk/kiosk';
import { FRAME_THEME, FRAME_THEME_REQUEST, ThemeService } from '@core/services/theme/theme.service';
import { SaveStateRepository } from '@data/save-state/save-state.store';
import { UserRepository } from '@data/user/user.store';
import { trackAnalyticsEvent } from '@shared/util/analytics.util';
import { downloadFile, FRAME_DOWNLOAD } from '@shared/util/file.util';
import { AppRouteNames } from '../../../app.routing.names';

/** The job app asks its fair's app to start over (its "Neu starten", CREW-1554). */
export const FRAME_RESTART = 'recrewt-frame-restart';

/** The job app tells its fair's app that its matches are on screen. */
export const FRAME_MATCHES = 'recrewt-frame-matches';

const CHOSEN_KEY = 'strandChosen';
const JOBS_ACTIVE_KEY = 'jobsTabActive';
const PUPIL_KEY = 'strandPupil';
const JOBS_OPENED_KEY = 'jobsTabOpened';
const HINT_SHOWN_KEY = 'jobsHintShown';
const FAIR_OPENED_KEY = 'fairTabOpened';
const FAIR_HINT_SHOWN_KEY = 'fairHintShown';

/**
 * The main tabs of an anymatch fair with a job app (`app.jobsTab`): this app's offers and the
 * fair's RECREWT job app. They appear once the visitor answered the choice question. The job app
 * runs in an iframe that stays mounted once opened, so both apps keep their place while the
 * visitor switches between them. Pupils get the job app alone, without tabs.
 */
@Injectable({ providedIn: 'root' })
export class FairTabsService {
  readonly config = inject(APP_CONFIG).app.jobsTab ?? null;

  /** Whether the visitor answered "Was möchten Sie zuerst anschauen?"; the tabs show from then on. */
  readonly chosen = signal(!!this.config && localStorage.getItem(CHOSEN_KEY) === '1');

  /** A pupil (`jobsTab.pupil`): the job app alone, at its apprenticeship path, without main tabs. */
  readonly pupil = signal(!!this.config?.pupil && localStorage.getItem(PUPIL_KEY) === '1');

  readonly jobsActive = signal(
    this.chosen() && (this.pupil() || sessionStorage.getItem(JOBS_ACTIVE_KEY) === '1'),
  );

  /** The job app's address for this visitor, once the jobs tab was opened. */
  readonly jobsFrameSrc = signal<string | null>(this.jobsActive() ? this.frameSrc() : null);

  /** The mascot points out the jobs tab (`jobsTab.hint`). */
  readonly hint = signal(false);

  /** The mascot points out the fair tab (`jobsTab.hint.fairText`, CREW-1488). */
  readonly fairHint = signal(false);

  /** The job app said its matches are on screen. */
  private readonly jobsOnMatches = signal(false);

  private readonly router = inject(Router);

  private readonly userRepo = inject(UserRepository);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  private readonly onStartPage = computed(() => this.url().startsWith(`/${AppRouteNames.HOME}`));

  /** The main tabs: from the choice question on, but not for pupils and not on the start page. */
  readonly showTabs = computed(() => this.chosen() && !this.pupil() && !this.onStartPage());

  /**
   * The job app is on screen: its tab is open or it is a pupil's whole app, but never over the start
   * page, where a returning visitor may start over (CREW-1498).
   */
  readonly frameShown = computed(() => this.jobsActive() && !this.onStartPage());

  constructor() {
    // a logout (kiosk reset, "Neu starten", starting over on the start page) hands the device to the next visitor
    this.userRepo.loggedOut$.subscribe(() => this.reset());

    // the story saves after every step: once the role or choice question is answered, the tabs follow
    const saves = inject(SaveStateRepository).selectedSaveState$;
    const picked = (questionId: string) =>
      saves.pipe(map((save) => save?.matchData?.customResponses?.[questionId]?.selectedIndices));
    const pupil = this.config?.pupil;
    if (pupil) {
      picked(pupil.questionId)
        .pipe(filter((indices) => !!indices?.includes(pupil.optionIndex) && !this.pupil()))
        .subscribe(() => this.choosePupil());
    }
    const choice = this.config?.choice;
    if (choice) {
      picked(choice.questionId)
        .pipe(filter((indices): indices is number[] => !!indices?.length && !this.chosen()))
        .subscribe((indices) => this.choose(indices.includes(choice.optionIndex)));
    }

    // once per visitor, a while after the fair's matches show, unless the jobs tab was opened
    const hint = this.config?.hint;
    if (hint) {
      const onFairMatches = computed(
        () =>
          this.showTabs() &&
          !this.jobsActive() &&
          this.url().startsWith(`/${AppRouteNames.MATCHING}`),
      );
      toObservable(onFairMatches)
        .pipe(
          switchMap((on) => (on && this.hintDue() ? timer(hint.afterSeconds * 1000) : EMPTY)),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          localStorage.setItem(HINT_SHOWN_KEY, '1');
          this.hint.set(true);
        });
    }

    // the other way round: once per visitor, a while after the job app's matches show, unless the
    // fair tab was open since the tabs appeared (CREW-1488)
    if (hint?.fairText) {
      const onJobMatches = computed(
        () => this.showTabs() && this.frameShown() && this.jobsOnMatches(),
      );
      toObservable(onJobMatches)
        .pipe(
          switchMap((on) => (on && this.fairHintDue() ? timer(hint.afterSeconds * 1000) : EMPTY)),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          localStorage.setItem(FAIR_HINT_SHOWN_KEY, '1');
          this.fairHint.set(true);
        });
    }

    // the job app hands its PDFs to this page to save (see `saveDownloadsVia`) and follows this
    // app's light/dark mode: it asks once it runs and hears of every change (see `ThemeService`)
    if (this.config) {
      const jobsOrigin = new URL(this.config.url).origin;
      const theme = inject(ThemeService);
      let jobsWindow: Window | null = null;
      const tellTheme = () =>
        jobsWindow?.postMessage({ type: FRAME_THEME, dark: theme.isDark }, jobsOrigin);
      fromEvent<MessageEvent>(window, 'message')
        .pipe(
          filter(({ origin }) => origin === jobsOrigin),
          takeUntilDestroyed(),
        )
        .subscribe(({ data, source }) => {
          if (data?.type === FRAME_DOWNLOAD && data.blob instanceof Blob) {
            downloadFile(data.blob, data.blob.type, String(data.filename));
          } else if (data?.type === FRAME_THEME_REQUEST) {
            jobsWindow = source as Window;
            tellTheme();
          } else if (data?.type === FRAME_RESTART) {
            // the job app's "Neu starten" starts the fair app over, as the fair tab's does
            this.userRepo.logoutUser();
            void this.router.navigate([AppRouteNames.HOME]);
          } else if (data?.type === FRAME_MATCHES) {
            this.jobsOnMatches.set(true);
          }
        });
      theme.isDark$.pipe(takeUntilDestroyed()).subscribe(() => tellTheme());
    }
  }

  /** Follows the answer to the choice question: jobs first opens the jobs tab. */
  private choose(jobsFirst: boolean): void {
    if (!this.config) {
      return;
    }
    localStorage.setItem(CHOSEN_KEY, '1');
    this.chosen.set(true);
    if (jobsFirst) {
      this.showJobs();
    } else {
      localStorage.setItem(FAIR_OPENED_KEY, '1');
    }
  }

  private choosePupil(): void {
    localStorage.setItem(PUPIL_KEY, '1');
    this.pupil.set(true);
    this.choose(true);
  }

  showJobs(): void {
    if (!this.config || this.jobsActive()) {
      return;
    }
    trackAnalyticsEvent('jobs-tab');
    localStorage.setItem(JOBS_OPENED_KEY, '1');
    this.hint.set(false);
    this.jobsFrameSrc.update((src) => src ?? this.frameSrc());
    this.jobsActive.set(true);
    sessionStorage.setItem(JOBS_ACTIVE_KEY, '1');
  }

  showFair(): void {
    localStorage.setItem(FAIR_OPENED_KEY, '1');
    this.fairHint.set(false);
    this.jobsActive.set(false);
    sessionStorage.removeItem(JOBS_ACTIVE_KEY);
  }

  dismissHint(): void {
    this.hint.set(false);
  }

  dismissFairHint(): void {
    this.fairHint.set(false);
  }

  private hintDue(): boolean {
    return !localStorage.getItem(HINT_SHOWN_KEY) && !localStorage.getItem(JOBS_OPENED_KEY);
  }

  private fairHintDue(): boolean {
    return !localStorage.getItem(FAIR_HINT_SHOWN_KEY) && !localStorage.getItem(FAIR_OPENED_KEY);
  }

  private reset(): void {
    this.chosen.set(false);
    this.pupil.set(false);
    this.jobsActive.set(false);
    this.jobsFrameSrc.set(null);
    this.hint.set(false);
    this.fairHint.set(false);
    this.jobsOnMatches.set(false);
  }

  private frameSrc(): string {
    const entry = this.pupil() ? `&entry=${this.config!.pupil!.entryOption}` : '';
    return `${this.config!.url}/matching?userId=${jobsKey()}${entry}`;
  }
}
